import { useCallback, useEffect, useRef, useState } from "react";

export function useCarousel(count: number, interval = 3000, resumeDelay = 7000) {
  const [index, setIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoRef = useRef<number | null>(null);
  const resumeRef = useRef<number | null>(null);
  const touchX = useRef(0);

  const go = useCallback(
    (n: number) => setIndex(((n % count) + count) % count),
    [count]
  );

  const stop = useCallback(() => {
    if (autoRef.current) {
      window.clearInterval(autoRef.current);
      autoRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    stop();
    autoRef.current = window.setInterval(() => setIndex((i) => (i + 1) % count), interval);
  }, [count, interval, stop]);

  const pauseThenResume = useCallback(() => {
    stop();
    if (resumeRef.current) window.clearTimeout(resumeRef.current);
    resumeRef.current = window.setTimeout(start, resumeDelay);
  }, [start, stop, resumeDelay]);

  const next = () => {
    go(index + 1);
    pauseThenResume();
  };
  const prev = () => {
    go(index - 1);
    pauseThenResume();
  };
  const goTo = (n: number) => {
    go(n);
    pauseThenResume();
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchX.current = e.changedTouches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const ex = e.changedTouches[0].clientX;
    if (Math.abs(ex - touchX.current) > 45) {
      ex < touchX.current ? go(index + 1) : go(index - 1);
      pauseThenResume();
    }
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
      start();
      return stop;
    }
    const io = new IntersectionObserver((en) => (en[0].isIntersecting ? start() : stop()), { rootMargin: "80px" });
    io.observe(el);
    return () => {
      io.disconnect();
      stop();
      if (resumeRef.current) window.clearTimeout(resumeRef.current);
    };
  }, [start, stop]);

  return { index, containerRef, next, prev, goTo, onTouchStart, onTouchEnd };
}
